import {
  showApiErrorNotification,
  useApiClient,
  useInvalidateHasuraQueries,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Loader, SegmentedControl, useMantineTheme } from '@mantine/core';
import { Box, Center, Text, rem } from '@mantine/core';
import { Group, Modal } from '@mantine/core';
import { useTanstackQuery } from '@vrplatform/data';
import { useCallback, useState } from 'react';
import { PlaidEmbeddedLink } from 'react-plaid-link';
import type { ConfirmBankConnectionsData } from './ConfirmBankConnectionsModal';
import { useNotifyOnPlaidEvent } from './useNotifyOnPlaidEvent';
import { usePlaidOnSuccessMutation } from '../../_hooks';
import { savePlaidTokenToLocalStorage } from '../../_utils';
import type { PlaidEnvironment } from '../../_types';
import { HiddenFeatureIndicator } from '../../../../components';
import { Button } from '@vrplatform/components';

type Props = {
  opened: boolean;
  closeModal: () => void;
  openSetupModal: (args: ConfirmBankConnectionsData) => void;
  openCsvModal: () => void;
};

type OnSuccess = ReturnType<typeof usePlaidOnSuccessMutation>['mutate'];

const PLAID_TOKEN_QUERY_KEY = 'select-bank-modal-plaid-connect';

export const SelectBankModal = ({
  opened,
  closeModal: cl,
  openCsvModal,
  openSetupModal,
}: Props) => {
  'use memo';

  const [environment, setEnvironment] =
    useState<PlaidEnvironment>('production');

  const invalidate = useInvalidateHasuraQueries();

  const closeModal = useCallback(() => {
    cl();
    invalidate([PLAID_TOKEN_QUERY_KEY]);
  }, [cl, invalidate]);

  const onBankAccountConnected = useCallback<
    Parameters<typeof usePlaidOnSuccessMutation>['0']['onSuccess']
  >(
    (args) => {
      closeModal();
      openSetupModal(args);
    },
    [closeModal, openSetupModal]
  );

  const { mutate, loading, error, reset } = usePlaidOnSuccessMutation({
    onSuccess: onBankAccountConnected,
  });

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={
        <Group justify="space-between" w="100%">
          <Text inherit>Add your bank account</Text>
          <HiddenFeatureIndicator permission="vrp-admin">
            <SegmentedControl
              ml="auto"
              size="xs"
              data={[
                { label: 'Production', value: 'production' },
                { label: 'Sandbox', value: 'sandbox' },
              ]}
              value={environment}
              onChange={(value) => setEnvironment(value as PlaidEnvironment)}
            />
          </HiddenFeatureIndicator>
        </Group>
      }
      centered
      size={600}
      radius="md"
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      {loading || error ? (
        <Center
          sx={{
            minHeight: 400,
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            maxWidth: 380,
            marginInline: 'auto',
          }}
        >
          {loading ? (
            <>
              <Loader type="dots" c="gray" />

              <Text mb="xs" mt="sm">
                Confirming credentials...
              </Text>
              <Text size="xs" c="gray">
                This can take a minute. Please don't close the page.
              </Text>
            </>
          ) : (
            <>
              <Icon
                icon="AlertTriangleIcon"
                size={24}
                color={(theme) => theme.colors.orange[6]}
              />

              <Text mb="xs" mt="sm" size="md">
                We failed to add the bank connection
              </Text>
              <Text size="xs" c="gray" mb="md">
                {error?.message ||
                  'The bank connection failed. Please try again and contact support if the issue persists.'}
              </Text>
              <Button onClick={reset}>Try again</Button>
            </>
          )}
        </Center>
      ) : (
        <>
          <Embedded
            opened={opened}
            onSuccess={mutate}
            environment={environment}
          />
          <Csv onClick={openCsvModal} />
        </>
      )}
    </Modal>
  );
};

function usePlaidEmbedTokenQuery(
  environment: PlaidEnvironment,
  enabled: boolean
) {
  'use memo';

  const $api = useApiClient();
  const [{ id: teamId }] = useTeam();

  const { data: token, isLoading: loadingToken } = useTanstackQuery({
    queryKey: [PLAID_TOKEN_QUERY_KEY, teamId, environment],
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const result = await $api.POST('/plaid/connect', {
        body: {
          environment,
        },
      });

      if (result.error) {
        showApiErrorNotification({
          error: result.error,
          defaultMessage: 'We failed to connect to the bank account provider',
          title: 'Connection failed',
        });

        throw result.error;
      }

      const token = result.data.token;
      savePlaidTokenToLocalStorage(teamId, token, environment);
      return token;
    },
  });

  return {
    token: token ?? null,
    loadingToken,
  };
}

const plaidStyles = {
  height: '400px',
  width: '100%',
};

const Embedded = ({
  opened,
  onSuccess,
  environment,
}: {
  opened: boolean;
  onSuccess: OnSuccess;
  environment: PlaidEnvironment;
}) => {
  'use memo';

  const { token, loadingToken } = usePlaidEmbedTokenQuery(environment, opened);

  const onEvent = useNotifyOnPlaidEvent();

  const plaidOnSuccess = useCallback(
    (code: string) => onSuccess(code, environment, token!),
    [onSuccess, environment, token]
  );

  return (
    <>
      {(loadingToken || !token) && (
        <Center mih={400}>
          <LoadingIndicator size="sm" />
        </Center>
      )}
      {token && (
        <PlaidEmbeddedLink
          token={token}
          onSuccess={plaidOnSuccess}
          style={plaidStyles}
          onEvent={onEvent}
        />
      )}
    </>
  );
};

const Csv = (props: { onClick: Props['openCsvModal'] }) => {
  'use memo';

  const theme = useMantineTheme();

  const padding = theme.spacing.sm;

  return (
    <Group
      w="100%"
      mt="sm"
      component={'button'}
      onClick={props.onClick}
      wrap="nowrap"
      sx={(theme) => ({
        boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.06)',
        backgroundColor: 'white',
        ':hover': {
          backgroundColor: theme.colors.gray[0],
        },
        border: `1px solid ${theme.colors.gray[1]}`,
        cursor: 'pointer',
        borderRadius: theme.radius.xl,
        outline: 'none',
        padding,
      })}
    >
      <Center
        sx={(theme) => ({
          padding: rem(8),
          border: `1px solid ${theme.colors.neutral[3]}`,
          borderRadius: theme.radius.xl,
          backgroundColor: 'white',
        })}
      >
        <Icon
          icon="FileDownloadIcon"
          color={(theme) => theme.colors[theme.primaryColor][6]}
          size={22}
        />
      </Center>
      <Box
        sx={() => ({
          textAlign: 'left',
          maxWidth: `calc(100% - ${padding} - ${padding} - 26px)`,
        })}
      >
        <Text fw={500} lineClamp={1}>
          Import bank records with CSV
        </Text>
        <Text
          display="block"
          component="span"
          c="gray"
          size="xs"
          truncate
          lineClamp={1}
        >
          Cannot find your bank institution? Don't worry we got you covered.
        </Text>
      </Box>
    </Group>
  );
};
